import React from "react";
import { motion } from "framer-motion";

export default function Home() {
  return (
    <main className="bg-slate-900 text-white min-h-screen overflow-hidden selection:bg-aseBlue selection:text-white">
      {/* 🌟 Hero Section - Abstract & Typographic */}
      <section className="relative w-full min-h-screen flex flex-col items-center justify-center px-4 pt-20">
        {/* Background glow effects */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-aseBlue/20 rounded-full blur-[120px] opacity-50 pointer-events-none"></div>
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-purple-500/10 rounded-full blur-[100px] pointer-events-none"></div>
        
        <div className="z-10 text-center max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <span className="inline-block py-1 px-3 rounded-full bg-white/5 border border-white/10 text-aseBlue text-sm font-semibold mb-6 tracking-wide uppercase">
              Engineering the Future
            </span>
          </motion.div>
          
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut", delay: 0.1 }}
            className="text-5xl md:text-7xl font-display font-bold tracking-tight mb-6 leading-tight"
          >
            Building Digital Solutions That Shape <span className="text-transparent bg-clip-text bg-gradient-to-r from-aseBlue to-blue-400">Tomorrow.</span>
          </motion.h1>
          
          <motion.p 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
            className="text-lg md:text-xl text-gray-400 mb-10 max-w-2xl mx-auto font-light"
          >
            ALLIED SOFTWARE ENGINEERS (PRIVATE) LIMITED empowers global enterprises with scalable, secure, and intelligent technology. We turn complex problems into elegant software.
          </motion.p>
          
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut", delay: 0.3 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <a href="#expertise" className="bg-aseBlue hover:bg-blue-600 text-white px-8 py-4 rounded-full font-medium transition duration-300 shadow-[0_0_20px_rgba(0,112,243,0.4)] hover:shadow-[0_0_30px_rgba(0,112,243,0.6)]">
              Explore Our Expertise
            </a>
            <a href="/contact" className="bg-white/5 hover:bg-white/10 border border-white/10 text-white px-8 py-4 rounded-full font-medium transition duration-300">
              Partner With Us
            </a>
          </motion.div>
        </div>
      </section>

      {/* 🚀 Marquee Section (Branches) */}
      <section className="py-10 border-y border-white/5 bg-slate-900/50 backdrop-blur-sm overflow-hidden relative">
        <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-slate-900 to-transparent z-10 pointer-events-none"></div>
        <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-slate-900 to-transparent z-10 pointer-events-none"></div>
        
        <div className="flex gap-16 whitespace-nowrap animate-marquee">
          {/* Duplicated for infinite loop effect */}
          {[...Array(4)].map((_, i) => (
            <div key={i} className="flex gap-16 items-center">
              <span className="text-gray-500 font-display font-medium text-xl uppercase tracking-widest">Lahore</span>
              <span className="w-2 h-2 rounded-full bg-aseBlue/50"></span>
              <span className="text-gray-500 font-display font-medium text-xl uppercase tracking-widest">Islamabad</span>
              <span className="w-2 h-2 rounded-full bg-aseBlue/50"></span>
              <span className="text-gray-500 font-display font-medium text-xl uppercase tracking-widest">Bahawalpur</span>
              <span className="w-2 h-2 rounded-full bg-aseBlue/50"></span>
              <span className="text-gray-500 font-display font-medium text-xl uppercase tracking-widest">Sahiwal</span>
              <span className="w-2 h-2 rounded-full bg-aseBlue/50"></span>
              <span className="text-gray-500 font-display font-medium text-xl uppercase tracking-widest">Karachi</span>
              <span className="w-2 h-2 rounded-full bg-aseBlue/50"></span>
              <span className="text-gray-500 font-display font-medium text-xl uppercase tracking-widest">Multan</span>
              <span className="w-2 h-2 rounded-full bg-aseBlue/50"></span>
              <span className="text-gray-500 font-display font-medium text-xl uppercase tracking-widest">Khanewal</span>
              <span className="w-2 h-2 rounded-full bg-aseBlue/50"></span>
              <span className="text-gray-500 font-display font-medium text-xl uppercase tracking-widest">Kabirwala</span>
              <span className="w-2 h-2 rounded-full bg-aseBlue/50"></span>
            </div>
          ))}
        </div>
      </section>

      {/* 🧩 Bento Box Expertise Section */}
      <section id="expertise" className="max-w-7xl mx-auto px-4 py-32 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-display font-bold mb-4">Core Expertise</h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">Delivering robust technical solutions across the entire software stack.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 auto-rows-[300px]">
          {/* Card 1: Spans 2 columns on desktop */}
          <motion.div 
            whileHover={{ y: -5 }}
            className="md:col-span-2 bg-gradient-to-br from-white/5 to-transparent border border-white/10 rounded-3xl p-8 relative overflow-hidden group"
          >
            <div className="absolute top-0 right-0 p-8 text-6xl opacity-20 group-hover:opacity-40 transition-opacity duration-500">💻</div>
            <h3 className="text-2xl font-display font-semibold mb-3">Custom Software & Product Engineering</h3>
            <p className="text-gray-400 max-w-md">Building scalable, tailor-made software products that drive innovation and growth. From initial architecture to deployment, we build it right.</p>
            <div className="absolute bottom-8 left-8 flex gap-2">
              <span className="px-3 py-1 bg-white/5 rounded-full text-xs text-gray-300 border border-white/10">React</span>
              <span className="px-3 py-1 bg-white/5 rounded-full text-xs text-gray-300 border border-white/10">Node.js</span>
              <span className="px-3 py-1 bg-white/5 rounded-full text-xs text-gray-300 border border-white/10">Python</span>
            </div>
          </motion.div>

          {/* Card 2 */}
          <motion.div 
            whileHover={{ y: -5 }}
            className="bg-gradient-to-br from-aseBlue/10 to-transparent border border-white/10 rounded-3xl p-8 relative overflow-hidden group"
          >
            <div className="absolute top-0 right-0 p-8 text-6xl opacity-20 group-hover:opacity-40 transition-opacity duration-500">☁️</div>
            <h3 className="text-2xl font-display font-semibold mb-3">Cloud & DevOps</h3>
            <p className="text-gray-400">Seamless cloud infrastructure, deployment automation, and performance optimization for zero downtime.</p>
          </motion.div>

          {/* Card 3 */}
          <motion.div 
            whileHover={{ y: -5 }}
            className="bg-gradient-to-br from-purple-500/10 to-transparent border border-white/10 rounded-3xl p-8 relative overflow-hidden group"
          >
            <div className="absolute top-0 right-0 p-8 text-6xl opacity-20 group-hover:opacity-40 transition-opacity duration-500">📱</div>
            <h3 className="text-2xl font-display font-semibold mb-3">Mobile + Web Apps</h3>
            <p className="text-gray-400">High-performance applications designed for a smooth, intuitive, and engaging user experience across all devices.</p>
          </motion.div>

          {/* Card 4: Spans 2 columns on desktop */}
          <motion.div 
            whileHover={{ y: -5 }}
            className="md:col-span-2 bg-gradient-to-br from-white/5 to-transparent border border-white/10 rounded-3xl p-8 relative overflow-hidden group flex flex-col justify-end"
          >
            <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-aseBlue/20 via-transparent to-transparent opacity-50"></div>
            <h3 className="text-2xl font-display font-semibold mb-3 relative z-10">AI & Machine Learning Integration</h3>
            <p className="text-gray-400 max-w-md relative z-10">Leveraging advanced AI models to automate workflows, extract insights, and create smarter applications for tomorrow's challenges.</p>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
